/*
 * -----------------------------------------------------------------------
 *  <copyright file="grid-filters.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import { DropDownList, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { GridColumnMenuFilterUIProps } from '@progress/kendo-react-grid';
import React, { useEffect, useRef } from 'react';

import DatePicker from './date-picker';
import { useTranslation } from '@hooks/index';

import { unixDateFromUtc } from '@utils/convertor';
import { titleCase } from '@utils/shared';
import { NullableComponentRef, setAttribute } from '@utils/automation-test-helpers';

const buildTestId = (id: string) => `grid-v2-filter-${id}`;

export const GridColumnMenuBooleanFilterV2 = ({ firstFilterProps }: any) => {
    const { t } = useTranslation();

    const onChange = (event: any) => {
        firstFilterProps.onChange({
            value: event.target.value === 'true',
            operator: 'eq',
            syntheticEvent: event.syntheticEvent,
        });
    };

    return (
        <>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <input
                    id="bool-true"
                    name="boolean"
                    type="radio"
                    value="true"
                    checked={firstFilterProps.value === true}
                    onChange={onChange}
                    data-testid={buildTestId('boolean-true')}
                />
                <label htmlFor="bool-true">&nbsp;{t('yes')}</label>
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <input
                    id="bool-false"
                    name="boolean"
                    type="radio"
                    value="false"
                    checked={firstFilterProps.value === false}
                    onChange={onChange}
                    data-testid={buildTestId('boolean-false')}
                />
                <label htmlFor="bool-false">&nbsp;{t('no')}</label>
            </div>
        </>
    );
};

export const GridColumnMenuActiveFilterV2 = ({ firstFilterProps }: any) => {
    const { t } = useTranslation();
    const controls = [t('yes'), t('no')];
    const onChange = (event: any) => {
        firstFilterProps.onChange({
            value: event.target.value === t('yes'),
            operator: 'eq',
            syntheticEvent: event.syntheticEvent,
        });
    };

    const itemRender = (li: any) => {
        const children = <>{li.props.children}</>;

        return React.cloneElement(li, li.props, children);
    };

    const ref = useRef<NullableComponentRef>(null);

    useEffect(() => setAttribute(ref, 'data-testid', buildTestId('active')));

    return (
        <DropDownList
            ref={ref}
            onChange={onChange}
            value={
                firstFilterProps.value === true ? (
                    <span className="capitalize">{t('yes')}</span>
                ) : firstFilterProps.value === false ? (
                    <span className="capitalize">{t('no')}</span>
                ) : undefined
            }
            itemRender={itemRender}
            defaultValue={<span className="capitalize">{t('selectState')}</span>}
            data={controls}
        />
    );
};

export const GridColumnEarliestLatestFilter = ({ firstFilterProps }: GridColumnMenuFilterUIProps) => {
    const { t } = useTranslation();

    const controls = [t('admin:earliest'), t('admin:latest')];
    const defaultItem = t('selectState');

    const hasValue = (val: string) => Boolean(val && val !== defaultItem);

    const onSelectChange = (event: DropDownListChangeEvent) => {
        const checkValue = hasValue(event.target.value);
        firstFilterProps.onChange({
            value: checkValue ? event.target.value : '',
            operator: checkValue ? 'eq' : '',
            syntheticEvent: event.syntheticEvent,
        });
    };

    const ref = useRef<NullableComponentRef>(null);

    useEffect(() => setAttribute(ref, 'data-testid', buildTestId('earliest-latest')));

    return (
        <DropDownList
            ref={ref}
            data={controls}
            onChange={onSelectChange}
            value={firstFilterProps.value}
            defaultValue={defaultItem}
        />
    );
};

export const GridColumnTimeUnitFilter = ({ firstFilterProps }: GridColumnMenuFilterUIProps) => {
    const { t } = useTranslation();

    let controls = [t('days'), t('hours'), t('minutes'), t('months'), t('none'), t('weeks'), t('years')];
    controls = controls.map((item) => titleCase(item));
    const defaultItem = t('selectState');

    const hasValue = (val: string) => Boolean(val && val !== defaultItem);

    const onSelectChange = (event: DropDownListChangeEvent) => {
        const checkValue = hasValue(event.target.value);
        firstFilterProps.onChange({
            value: checkValue ? event.target.value : '',
            operator: checkValue ? 'eq' : '',
            syntheticEvent: event.syntheticEvent,
        });
    };

    const itemRender = (li: any) => {
        const children = <>{li.props.children}</>;
        const props = {
            ...li.props,
        };
        return React.cloneElement(li, props, children);
    };

    const ref = useRef<NullableComponentRef>(null);

    useEffect(() => setAttribute(ref, 'data-testid', buildTestId('time-unit')));

    return (
        <DropDownList
            ref={ref}
            data={controls}
            onChange={onSelectChange}
            value={firstFilterProps.value}
            defaultValue={defaultItem}
            itemRender={itemRender}
        />
    );
};

const filterDate = (selectedState: string, currentDate: any, date: any) => {
    if (selectedState === 'Is equal to') return currentDate === date;
    if (selectedState === 'Is after') return currentDate > date;
    if (selectedState === 'Is before') return currentDate < date;
    if (selectedState === 'Is not null') return currentDate;
    if (selectedState === '') return true;
};
export const GridColumnDateFilterV2 = ({ firstFilterProps }: any) => {
    const values = ['Is equal to', 'Is after', 'Is before', 'Is not null', 'Is null'];
    const valuesBoolean = ['And', 'Or'];

    // note: formatDate needs to compare dates without time. so time just sets to 00:00:00
    const handleOperator = (current: any, next: any) => {
        const logicOperator = (next?.logic || valuesBoolean[0]) === 'And' ? '&&' : '||';
        // If filter is set to "is null" or "is not null" or vise-versa return all records
        const nullNotNullCheck =
            (next.dropdown === values[4] && next?.secondDropdown === values[3]) ||
            (next.dropdown === values[3] && next?.secondDropdown === values[4]);
        const firstDate = unixDateFromUtc(next?.firstDate);
        const secondDate = unixDateFromUtc(next?.secondDate);
        const currentValue = unixDateFromUtc(current, true);
        const first = filterDate(next?.dropdown || values[0], currentValue, firstDate);
        const second = filterDate(next?.secondDropdown || values[0], currentValue, secondDate);
        const selectedState = (next?.firstDate ? next?.dropdown : next?.secondDropdown) || values[0];
        let date = next?.firstDate ?? next?.secondDate;
        date = unixDateFromUtc(date);

        // Fist and Second dropdown Null/NotNull Check and clause is "OR" or "AND"
        if (nullNotNullCheck) {
            return logicOperator === '||';
        }
        // If First and Second dropdown is Null & Value
        if (
            (next.dropdown === values[4] && next?.secondDate) ||
            (next.secondDropdown === values[4] && next.firstDate)
        ) {
            return logicOperator === '&&' ? false : !unixDateFromUtc(current) || first || second;
        }
        // Final catch if only one value is null and the other values are not set
        if (next.dropdown === values[4] || next?.secondDropdown === values[4]) {
            return !unixDateFromUtc(current);
        }
        // first input or second has options is not null and date input doesn't have dates entered
        if (
            (next.dropdown === values[3] || next?.secondDropdown === values[3]) &&
            !next.firstDate &&
            !next.secondDate
        ) {
            return unixDateFromUtc(current);
        }
        // first input or second has options is not null and one of date inputs have dates entered
        if (
            (next.dropdown === values[3] || next?.secondDropdown === values[3]) &&
            (next?.firstDate || next?.secondDate)
        ) {
            if (logicOperator === '||') {
                return unixDateFromUtc(current);
            }
            return currentValue ? filterDate(selectedState, currentValue, date) : false;
        }

        // one of date inpus have date entered
        if ((next?.firstDate && !next?.secondDate) || (!next?.firstDate && next?.secondDate)) {
            return filterDate(selectedState, currentValue, date);
        }
        // If both values are populated
        if (next?.firstDate && next?.secondDate) {
            return logicOperator !== '||' ? first && second : first || second;
        }
    };

    const onChange = (key: string) => (event: any) => {
        firstFilterProps.onChange({
            value: {
                ...firstFilterProps.value,
                [key]: event.value,
            },
            operator: handleOperator,
            syntheticEvent: event.syntheticEvent,
        });
    };

    const itemBooleanRender = (li: any) => {
        const children = <>{li.props.children}</>;
        const props = {
            ...li.props,
        };
        return React.cloneElement(li, props, children);
    };

    const itemRender = (detail: string) => (li: any) => {
        const children = <>{li.props.children}</>;
        const props = {
            ...li.props,
        };
        return React.cloneElement(li, props, children);
    };

    const firstOperator = useRef<NullableComponentRef>(null);
    const andOr = useRef<NullableComponentRef>(null);
    const secondOperator = useRef<NullableComponentRef>(null);

    useEffect(() => {
        setAttribute(firstOperator, 'data-testid', buildTestId('first-operator'));
        setAttribute(andOr, 'data-testid', buildTestId('and-or'));
        setAttribute(secondOperator, 'data-testid', buildTestId('second-operator'));
    });

    return (
        <>
            <DropDownList
                ref={firstOperator}
                onChange={(event: any) => {
                    firstFilterProps.onChange({
                        value: {
                            ...firstFilterProps.value,
                            dropdown: event.target.value,
                        },
                        operator: handleOperator,
                        syntheticEvent: event.syntheticEvent,
                    });
                }}
                value={firstFilterProps.value?.dropdown || values[0]}
                itemRender={itemRender('first')}
                data={values}
            />
            <DatePicker
                handleDateChange={onChange('firstDate')}
                selectedDate={firstFilterProps.value?.firstDate}
                label=""
                customId="filter-first-date"
                data-testid={buildTestId('first-date')}
            />
            <DropDownList
                ref={andOr}
                onChange={(event: any) => {
                    firstFilterProps.onChange({
                        value: {
                            ...firstFilterProps.value,
                            logic: event.target.value,
                        },
                        operator: handleOperator,
                        syntheticEvent: event.syntheticEvent,
                    });
                }}
                value={firstFilterProps.value?.logic || valuesBoolean[0]}
                itemRender={itemBooleanRender}
                data={valuesBoolean}
            />
            <DropDownList
                ref={secondOperator}
                onChange={(event: any) => {
                    firstFilterProps.onChange({
                        value: {
                            ...firstFilterProps.value,
                            secondDropdown: event.target.value,
                        },
                        operator: handleOperator,
                        syntheticEvent: event.syntheticEvent,
                    });
                }}
                value={firstFilterProps.value?.secondDropdown || values[0]}
                itemRender={itemRender('three')}
                data={values}
            />
            <DatePicker
                handleDateChange={onChange('secondDate')}
                selectedDate={firstFilterProps.value?.secondDate}
                label=""
                customId="filter-second-date"
                data-testid={buildTestId('second-date')}
            />
        </>
    );
};
